import { useParams } from "@tanstack/react-router";
import { PrinterIcon } from "lucide-react";
import { useTitle } from "react-use";

import { trpc } from "@blis/trpc/react";

import { CommandMenuProvider } from "@/components/CommandMenu";
import { LoadingPage } from "@/components/LoadingPage";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Centered } from "@/components/ui/container";
import { Separator } from "@/components/ui/separator";
import { useInitializeNavStack } from "@/utils/useNavStack";
import { PrintWorklistsButton } from "../components/PrintWorklistsButton";
import { TestRunActions } from "../components/TestRunActions";
import { TestRunDetail } from "../components/TestRunDetail";
import { TestRunProgress } from "../components/TestRunProgress";
import { WorklistList } from "../components/WorklistList";

export function TestRunDetails() {
  useInitializeNavStack();
  const params = useParams({ from: "/_auth/_app/runs/$id/" });
  useTitle(`Test run ${params.id}`);

  const run = trpc.runs.get.useQuery({ id: params.id });

  if (run.isPending) {
    return <LoadingPage />;
  }

  if (!run.data) {
    return <Centered>Test run not found</Centered>;
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex flex-col flex-1">
        <SubHeader>
          <SubHeader.Left>
            <Breadcrumbs>
              <Breadcrumb href={{ to: "/runs" }}>Test runs</Breadcrumb>
              <Breadcrumb>{run.data?.id}</Breadcrumb>
            </Breadcrumbs>
          </SubHeader.Left>
          <SubHeader.Right>
            <PrintWorklistsButton
              run={run.data}
              worklists={run.data.testSpecs || []}
              variant="outline"
              size="sm"
            >
              <PrinterIcon />
              Print all
            </PrintWorklistsButton>
          </SubHeader.Right>
        </SubHeader>
        <div className="flex-1 overflow-y-auto">
          <WorklistList
            worklists={run.data.testSpecs || []}
            run={run.data}
            loading={run.isPending}
          />
        </div>
        <CommandMenuProvider>
          <TestRunActions run={run.data} worklists={run.data.testSpecs || []} />
        </CommandMenuProvider>
      </div>
      <div className="flex-col gap-4 w-[300px] shrink-0 p-4 border-l hidden lg:flex">
        <TestRunProgress run={run.data} />
        <Separator />
        <TestRunDetail run={run.data} />
      </div>
    </div>
  );
}
